import { parse, renderHTML } from "@djot/djot";
import { AnsiUp } from "ansi_up";
import DOMPurify from "dompurify";

const ansi = new AnsiUp();
ansi.use_classes = true;

// For post-HTML-render ANSI passes: don't re-escape existing HTML markup.
const ansiPostRender = new AnsiUp();
ansiPostRender.use_classes = true;
ansiPostRender.escape_html = false;

const ALLOWED_TAGS = [
    "p", "br", "div", "span", "strong", "b", "em", "i", "u", "s",
    "ul", "ol", "li", "dl", "dt", "dd", "a", "pre", "code",
    "blockquote", "h1", "h2", "h3", "h4", "h5", "h6", "hr",
    "table", "thead", "tbody", "tr", "td", "th", "small", "sup", "sub",
];

const ALLOWED_ATTR = [
    "href", "class", "id", "alt", "title", "style", "target", "rel",
    "data-objid", "tabindex", "role", "aria-label",
];

function autoLink(html: string): string {
    return html.replace(
        /(?<!['"=])(https?:\/\/[^\s<>"']+)/g,
        '<a href="$1" class="moo-link-external" target="_blank" rel="noopener noreferrer">$1</a>',
    );
}

export function renderContent(content: unknown, contentType: string): string {
    const text =
        typeof content === "string" ? content
        : Array.isArray(content) ? content.join("\n")
        : String(content ?? "");

    let html: string;

    if (contentType === "text/djot") {
        const doc = parse(text, { sourcePositions: false });
        html = ansiPostRender.ansi_to_html(renderHTML(doc));
    } else if (contentType === "text/html") {
        html = text;
    } else {
        // text/plain — run through ANSI processor, then wrap in a span
        const ansiHtml = ansi.ansi_to_html(text);
        html = `<span class="plain">${ansiHtml}</span>`;
    }

    html = autoLink(html);
    // Extend allowed URI schemes to include moo:// (used for clickable commands/inspects).
    const ALLOWED_URI_REGEXP =
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|moo):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP });
}

export function renderTraceback(text: string): string {
    const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return `<pre class="traceback">${escaped}</pre>`;
}
